/**
 * Página de detalhes e monitoramento de campanha (Redesign Moderno)
 * Exibe estatísticas em tempo real, progresso e lista de destinatários
 * Implementa Realtime updates + auto-refresh durante execução
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  ArrowLeft, Play, Pause, StopCircle, RefreshCw,
  Send, CheckCheck, Eye, XCircle, Clock, Users,
  MessageSquare, Settings2, AlertTriangle,
  FileText, Image, Video, Music, Timer,
  ScrollText, Inbox, Package, Calendar, Hash, User,
  ChevronDown, Zap, ListChecks
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { Database } from '@/integrations/supabase/types';
import { logger } from '@/lib/logger';
import { cn } from '@/lib/utils';

// Componentes modernos
import { CampaignMetricCard } from '@/components/tenant/campaigns/CampaignMetricCard';
import { CampaignProgressBar } from '@/components/tenant/campaigns/CampaignProgressBar';
import { CampaignExecutionLogs } from '@/components/tenant/campaigns/CampaignExecutionLogs';
import { RecipientTable, type RecipientRow } from '@/components/tenant/campaigns/RecipientTable';
import { RecipientExportButton } from '@/components/tenant/campaigns/RecipientExportButton';

import { ChatConversationModal } from '@/components/tenant/kanban/ChatConversationModal';
// Modal de preview da lista importada (mesmo usado na Central de Importação de Listas)
import ListPreviewDialog from '@/components/tenant/lists/ListPreviewDialog';
import { 
  CampaignActionRecord, 
  campaignActionConfig, 
  executionConditionConfig 
} from '@/components/tenant/campaigns/campaign-actions-types';

// Tipos
type Campaign = Database['public']['Tables']['campaigns']['Row'];
type CampaignMessageItem = Database['public']['Tables']['campaign_message_items']['Row'];

// Interface para estatísticas
// v1.1: adicionado `processing` para refletir status intermediário do worker
interface CampaignStats {
  total: number;
  pending: number;
  processing: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
  skipped: number;
}

// Interface para grupos de mensagens
interface MessageGroup {
  id: string;
  name: string;
  weight: number;
  sort_order: number;
}

// Interface para inboxes da campanha
interface CampaignInbox {
  inbox_id: string;
  inbox_name: string;
  send_weight: number;
}

// Configuração de status da campanha
const statusConfig: Record<string, {
  label: string;
  className: string;
  dotClass?: string;
}> = {
  draft: {
    label: 'Rascunho',
    className: 'bg-muted text-muted-foreground border-muted-foreground/30',
  },
  scheduled: {
    label: 'Agendada',
    className: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  },
  running: {
    label: 'Em execução',
    className: 'bg-green-500/10 text-green-600 border-green-500/30',
    dotClass: 'bg-green-500',
  },
  paused: {
    label: 'Pausada',
    className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30',
  },
  completed: {
    label: 'Concluída',
    className: 'bg-green-500/10 text-green-600 border-green-500/30',
  },
  cancelled: {
    label: 'Cancelada',
    className: 'bg-destructive/10 text-destructive border-destructive/30',
  },
};

// Mapa de ícones e cores por tipo de item de mensagem
const itemTypeConfig: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
  text: { icon: <FileText className="h-4 w-4" />, label: 'Texto', color: 'bg-blue-500/10 text-blue-600' },
  image: { icon: <Image className="h-4 w-4" />, label: 'Imagem', color: 'bg-green-500/10 text-green-600' },
  video: { icon: <Video className="h-4 w-4" />, label: 'Vídeo', color: 'bg-purple-500/10 text-purple-600' },
  audio: { icon: <Music className="h-4 w-4" />, label: 'Áudio', color: 'bg-pink-500/10 text-pink-600' },
  ptv: { icon: <Video className="h-4 w-4" />, label: 'Vídeo Circular', color: 'bg-indigo-500/10 text-indigo-600' },
  gif: { icon: <Image className="h-4 w-4" />, label: 'GIF', color: 'bg-cyan-500/10 text-cyan-600' },
  document: { icon: <FileText className="h-4 w-4" />, label: 'Documento', color: 'bg-orange-500/10 text-orange-600' },
  delay: { icon: <Timer className="h-4 w-4" />, label: 'Delay', color: 'bg-muted text-muted-foreground' },
  template: { icon: <ScrollText className="h-4 w-4" />, label: 'Template', color: 'bg-emerald-500/10 text-emerald-600' },
};

// Componente para exibir preview de item de mensagem (estilo chat)
function MessageItemPreview({ item, index }: { item: CampaignMessageItem; index: number }) {
  const config = itemTypeConfig[item.item_type] || itemTypeConfig.text;
  
  // Gera URL pública para itens do Storage
  const getPublicUrl = (path: string | null) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/anexos/${path}`;
  };

  // Renderiza delay de forma especial
  if (item.item_type === 'delay') {
    return (
      <div className="flex items-center justify-center gap-2 py-2">
        <div className="h-px flex-1 bg-border" />
        <Badge variant="outline" className="gap-1 bg-muted/50">
          <Timer className="h-3 w-3" />
          Aguardar {item.delay_seconds || 0}s
        </Badge>
        <div className="h-px flex-1 bg-border" />
      </div>
    );
  }

  // Renderiza template de forma especial
  // Renderiza template de forma especial
  if (item.item_type === 'template') {
    // Tipagem atualizada para suportar estrutura complexa do WhatsApp Business API
    const templateParams = item.template_params as {
      body_params?: (string | { type?: string; text?: string })[];
      header_params?: (string | { type?: string; text?: string })[];
      button_params?: { type?: string; index?: number; sub_type?: string; parameters?: unknown[] }[];
    } | null;

    // Função auxiliar para extrair valor de parâmetro (string ou objeto)
    const getParamValue = (param: string | { type?: string; text?: string } | undefined): string => {
      if (!param) return '(vazio)';
      if (typeof param === 'string') return param || '(vazio)';
      if (typeof param === 'object' && param.text) return param.text;
      return '(vazio)';
    };

    // Função auxiliar para renderizar button_params (estrutura complexa)
    const getButtonParamDisplay = (btn: { type?: string; index?: number; sub_type?: string; parameters?: unknown[] }): string => {
      if (btn.sub_type === 'url' && Array.isArray(btn.parameters) && btn.parameters.length > 0) {
        const firstParam = btn.parameters[0] as { type?: string; text?: string } | undefined;
        if (firstParam && typeof firstParam === 'object' && firstParam.text) {
          return `URL: ${firstParam.text}`;
        }
      }
      return btn.sub_type || btn.type || 'botão';
    };

    return (
      <div className="flex gap-3">
        {/* Número do item */}
        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
          {index + 1}
        </div>

        {/* Card do Template */}
        <div className="flex-1 max-w-md">
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl rounded-tl-sm p-3 shadow-sm">
            {/* Badge do tipo */}
            <Badge variant="outline" className="gap-1 mb-2 text-xs bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
              <ScrollText className="h-3 w-3" />
              Template WhatsApp Oficial
            </Badge>

            {/* Nome e Idioma */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium">{item.template_name}</span>
                <Badge variant="secondary" className="text-xs">
                  {item.template_language || 'pt_BR'}
                </Badge>
              </div>

              {/* Variáveis configuradas */}
              {templateParams && (
                <div className="space-y-2 mt-2 pt-2 border-t border-emerald-500/20">
                  {templateParams.body_params && templateParams.body_params.length > 0 && (
                    <div className="text-xs">
                      <span className="text-muted-foreground font-medium">Corpo:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {templateParams.body_params.map((param, i) => (
                          <Badge key={i} variant="outline" className="text-[10px] font-mono">
                            {`{{${i + 1}}}`} → {getParamValue(param)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {templateParams.header_params && templateParams.header_params.length > 0 && (
                    <div className="text-xs">
                      <span className="text-muted-foreground font-medium">Cabeçalho:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {templateParams.header_params.map((param, i) => (
                          <Badge key={i} variant="outline" className="text-[10px] font-mono">
                            {`{{${i + 1}}}`} → {getParamValue(param)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {templateParams.button_params && templateParams.button_params.length > 0 && (
                    <div className="text-xs">
                      <span className="text-muted-foreground font-medium">Botões:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {templateParams.button_params.map((btn, i) => (
                          <Badge key={i} variant="outline" className="text-[10px] font-mono">
                            [{btn.index ?? i}] {getButtonParamDisplay(btn)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3">
      {/* Número do item */}
      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
        {index + 1}
      </div>

      {/* Balão estilo WhatsApp */}
      <div className="flex-1 max-w-md">
        <div className="bg-muted rounded-2xl rounded-tl-sm p-3 shadow-sm">
          {/* Badge do tipo */}
          <Badge variant="outline" className={cn('gap-1 mb-2 text-xs', config.color)}>
            {config.icon}
            {config.label}
          </Badge>

          {/* Conteúdo de texto */}
          {item.text_content && (
            <p className="text-sm whitespace-pre-wrap break-words">
              {item.text_content}
            </p>
          )}

          {/* Preview de mídia */}
          {item.media_url && (
            <div className="mt-2">
              {item.item_type === 'image' && (
                <img
                  src={getPublicUrl(item.media_url) || ''}
                  alt="Preview"
                  className="max-h-40 rounded-lg object-cover"
                />
              )}
              {(item.item_type === 'video' || item.item_type === 'ptv') && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-background/50 rounded-lg p-2">
                  <Video className="h-4 w-4" />
                  <a
                    href={getPublicUrl(item.media_url) || ''}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline truncate"
                  >
                    {item.media_filename || 'Vídeo'}
                  </a>
                </div>
              )}
              {item.item_type === 'audio' && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-background/50 rounded-lg p-2">
                  <Music className="h-4 w-4" />
                  <a
                    href={getPublicUrl(item.media_url) || ''}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline truncate"
                  >
                    {item.media_filename || 'Áudio'}
                  </a>
                </div>
              )}
              {item.item_type === 'document' && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-background/50 rounded-lg p-2">
                  <FileText className="h-4 w-4" />
                  <a
                    href={getPublicUrl(item.media_url) || ''}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline truncate"
                  >
                    {item.media_filename || 'Documento'}
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Skeleton de carregamento
function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      {/* Header skeleton */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="space-y-2">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-9 w-24" />
        </div>
      </div>

      {/* Stats skeleton */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-xl" />
        ))}
      </div>

      {/* Progress skeleton */}
      <Skeleton className="h-48 rounded-xl" />

      {/* Table skeleton */}
      <Skeleton className="h-96 rounded-xl" />
    </div>
  );
}

/**
 * Props do conteúdo reutilizável de detalhes da campanha.
 * v2.0.0 — o mesmo conteúdo atende a rota (/tenant/campaigns/:id) e o modal
 * aberto a partir da aba "Campanhas" do modal de cliente/contato.
 */
interface CampaignDetailsContentProps {
  /** ID da campanha (quando ausente, usa o parâmetro da rota) */
  campaignId?: string;
  /** Contexto de renderização: página (rota) ou modal */
  variant?: 'page' | 'modal';
  /** Callback de fechamento (usado apenas no modal) */
  onClose?: () => void;
}

export function CampaignDetailsContent({
  campaignId,
  variant = 'page',
  onClose,
}: CampaignDetailsContentProps = {}) {
  const { id: routeId } = useParams<{ id: string }>();
  const id = campaignId ?? routeId;
  const navigate = useNavigate();
  // No modal, "voltar/sair" apenas fecha o modal (mantém o usuário no contexto anterior)
  const goBack = useCallback(() => {
    if (variant === 'modal') {
      onClose?.();
      return;
    }
    navigate('/tenant/campaigns');
  }, [variant, onClose, navigate]);
  const { currentTenant } = useAuth();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [recipients, setRecipients] = useState<RecipientRow[]>([]);
  const [messageItems, setMessageItems] = useState<CampaignMessageItem[]>([]);
  const [messageGroups, setMessageGroups] = useState<MessageGroup[]>([]);
  const [campaignInboxes, setCampaignInboxes] = useState<CampaignInbox[]>([]);
  const [campaignActions, setCampaignActions] = useState<CampaignActionRecord[]>([]);
  // Mapa de nomes resolvidos pelo servidor (tag_<id>, phase_<id>, etc.)
  const [actionResolvedNames, setActionResolvedNames] = useState<Record<string, string>>({});
  // Listas importadas usadas no disparo (origem dos destinatários) — RPC v1.2.0
  const [campaignLists, setCampaignLists] = useState<Array<{ id: string; name: string }>>([]);
  // Lista selecionada para abrir o modal de preview (mesmo modal da Importação de Listas)
  const [previewList, setPreviewList] = useState<any | null>(null);
  const [loadingPreviewListId, setLoadingPreviewListId] = useState<string | null>(null);

  /**
   * Abre o modal de detalhes da lista importada.
   * Reaproveita a RPC `get_campaign_lists` (mesma usada na Central de Listas)
   * para obter o registro completo exigido pelo ListPreviewDialog.
   */
  const handleOpenListPreview = useCallback(async (listId: string) => {
    if (!currentTenant?.id) return;
    setLoadingPreviewListId(listId);
    try {
      const { data, error } = await supabase.rpc('get_campaign_lists', {
        p_tenant_hint: currentTenant.id,
      });
      if (error) throw error;
      const found = (data as any[] | null)?.find((l) => l.id === listId);
      if (!found) {
        toast.error('Lista não encontrada ou sem permissão de acesso');
        return;
      }
      setPreviewList(found);
    } catch (err) {
      logger.error('[CampaignDetails] Erro ao abrir preview da lista', err);
      toast.error('Não foi possível abrir os detalhes da lista');
    } finally {
      setLoadingPreviewListId(null);
    }
  }, [currentTenant]);

  const [stats, setStats] = useState<CampaignStats>({
    total: 0, pending: 0, processing: 0, sent: 0, delivered: 0, read: 0, failed: 0, skipped: 0
  });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // v1.1: Aba ativa controlada — usada para polling contextual e refresh cirúrgico
  const [activeTab, setActiveTab] = useState<string>('recipients');

  // Filtros da listagem de destinatários (controlados → enviados à RPC)
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  // Paginação server-side da aba Destinatários (v1.2 — evita baixar 1000 linhas)
  const [recipientsPage, setRecipientsPage] = useState(1);
  const [recipientsPageSize, setRecipientsPageSize] = useState(30);
  const [recipientsTotal, setRecipientsTotal] = useState(0);

  // Estado do modal de chat (abre na inbox de disparo do destinatário clicado)
  const [chatConversationId, setChatConversationId] = useState<string | null>(null);
  const [chatModalOpen, setChatModalOpen] = useState(false);

  // ============================================================
  // Carrega dados consolidados da campanha via RPC server-side
  // (substitui 9 SELECTs diretos em tabelas)
  // ============================================================
  const fetchCampaignDetails = useCallback(async () => {
    if (!id || !currentTenant) return;

    const { data, error } = await supabase.rpc('get_campaign_details', {
      p_campaign_id: id,
      p_tenant_hint: currentTenant.id,
    });

    if (error) {
      logger.error('CampaignDetails', `Erro ao carregar detalhes da campanha: ${error.message}`);
      toast.error('Erro ao carregar campanha');
      return;
    }

    // v1.1: `stats` agora vem agregado do servidor (evita baixar 100k linhas
    // no frontend só para contar). Chave nova no payload.
    const payload = data as unknown as {
      campaign: Campaign;
      message_items: CampaignMessageItem[];
      message_groups: MessageGroup[];
      inboxes: CampaignInbox[];
      actions: Array<{
        id: string;
        campaign_id: string;
        action_type: CampaignActionRecord['action_type'];
        action_value: CampaignActionRecord['action_value'];
        execution_condition: CampaignActionRecord['execution_condition'];
        sort_order: number;
        created_at?: string | null;
      }>;
      resolved_names: Record<string, string>;
      /** v1.2.0: listas importadas usadas no disparo */
      lists?: Array<{ id: string; name: string }>;
      stats?: {
        total?: number; pending?: number; processing?: number;
        sent?: number; delivered?: number; read?: number;
        failed?: number; skipped?: number;
      };
    } | null;


    if (!payload?.campaign) {
      toast.error('Campanha não encontrada');
      goBack();
      return;
    }

    setCampaign(payload.campaign);
    setMessageItems(payload.message_items || []);
    setMessageGroups(payload.message_groups || []);
    setCampaignInboxes(payload.inboxes || []);
    setCampaignActions(
      (payload.actions || []).map(a => ({
        id: a.id,
        campaign_id: a.campaign_id,
        action_type: a.action_type,
        action_value: a.action_value,
        execution_condition: a.execution_condition,
        sort_order: a.sort_order,
        created_at: a.created_at || undefined,
      }))
    );
    setActionResolvedNames(payload.resolved_names || {});
    setCampaignLists(payload.lists || []);


    // Aplica stats agregados do servidor (fallback para zeros se ausente)
    const s = payload.stats || {};
    setStats({
      total: Number(s.total ?? 0),
      pending: Number(s.pending ?? 0),
      processing: Number(s.processing ?? 0),
      sent: Number(s.sent ?? 0),
      delivered: Number(s.delivered ?? 0),
      read: Number(s.read ?? 0),
      failed: Number(s.failed ?? 0),
      skipped: Number(s.skipped ?? 0),
    });
  }, [id, currentTenant, navigate]);

  // ============================================================
  // Lista paginada/filtrada de destinatários (RPC com filtros)
  // ============================================================
  const fetchRecipients = useCallback(async () => {
    if (!id || !currentTenant) return;

    setRecipientsLoading(true);
    const { data, error } = await supabase.rpc('get_campaign_recipients', {
      p_campaign_id: id,
      p_status_filter: statusFilter,
      p_search: search || null,
      p_limit: recipientsPageSize,
      p_offset: (recipientsPage - 1) * recipientsPageSize,
      p_tenant_hint: currentTenant.id,
    });
    setRecipientsLoading(false);

    if (error) {
      logger.error('CampaignDetails', `Erro ao carregar destinatários: ${error.message}`);
      return;
    }

    const rows = (data || []) as unknown as RecipientRow[];
    setRecipients(rows);
    // total_count vem via COUNT(*) OVER () na RPC — usa 0 quando página vazia
    setRecipientsTotal(Number(rows[0]?.total_count ?? 0));
  }, [id, currentTenant, statusFilter, search, recipientsPage, recipientsPageSize]);

  // Reset de página quando filtros mudarem (v1.2)
  useEffect(() => {
    setRecipientsPage(1);
  }, [statusFilter, search]);

  // ============================================================
  // Refresh manual — contextual à aba ativa
  // - Sempre: detalhes + stats (via get_campaign_details)
  // - Só quando aba "Destinatários" ativa: também baixa recipients
  // - Logs têm refresh próprio dentro do CampaignExecutionLogs
  // ============================================================
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    const jobs: Promise<unknown>[] = [fetchCampaignDetails()];
    if (activeTab === 'recipients') jobs.push(fetchRecipients());
    await Promise.all(jobs);
    setIsRefreshing(false);
    toast.success('Dados atualizados');
  }, [fetchCampaignDetails, fetchRecipients, activeTab]);

  // Carregamento inicial (uma vez por tenant/campanha)
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCampaignDetails(), fetchRecipients()]);
      setLoading(false);
    };
    loadData();
  }, [fetchCampaignDetails, fetchRecipients]);

  // ============================================================
  // Auto-refresh v1.1 (surgical):
  // - Apenas quando campanha "running"
  // - Intervalo elevado de 3s → 15s (reduz carga durante disparos)
  // - Pausa quando aba do browser está oculta (document.hidden)
  // - Atualiza apenas o que a aba ativa precisa (recipients só se visível)
  // ============================================================
  useEffect(() => {
    if (campaign?.status !== 'running') return;
    logger.info('CampaignDetails', 'Auto-refresh ativo (15s, contextual)');

    const tick = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchCampaignDetails();
      if (activeTab === 'recipients') fetchRecipients();
    };

    const interval = setInterval(tick, 15000);
    return () => {
      logger.info('CampaignDetails', 'Parando auto-refresh');
      clearInterval(interval);
    };
  }, [campaign?.status, activeTab, fetchCampaignDetails, fetchRecipients]);

  // ============================================================
  // Realtime v1.1 (surgical):
  // - Mantido: UPDATE em `campaigns` (status, progresso, timestamps)
  // - REMOVIDO: assinatura em `campaign_recipients` (gerava enxurrada de
  //   eventos durante disparos, disparando dois fetches por evento).
  //   Substituído pelo polling contextual de 15s acima.
  // ============================================================
  useEffect(() => {
    if (!id) return;
    const campaignChannel = supabase
      .channel(`campaign-details-${id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'campaigns', filter: `id=eq.${id}` },
        () => {
          logger.info('CampaignDetails', 'Campanha atualizada via Realtime');
          fetchCampaignDetails();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(campaignChannel);
    };
  }, [id, fetchCampaignDetails]);

  // Abre o chat exatamente na inbox em que o destinatário foi disparado
  const handleRecipientClick = useCallback(async (recipient: RecipientRow) => {
    // Basta ter inbox + (contact_id OU phone). A RPC resolve contato existente
    // por telefone quando contact_id estiver NULL (campanhas legadas) — sem criar duplicado.
    if (!recipient.sent_via_inbox_id || (!recipient.contact_id && !recipient.phone)) {
      toast.error('Destinatário ainda não foi disparado');
      return;
    }
    const { data, error } = await supabase.rpc('get_campaign_recipient_conversation', {
      p_recipient_id: recipient.id,
      p_tenant_hint: currentTenant?.id,
    });
    if (error) {
      logger.error('CampaignDetails', `Erro ao abrir conversa: ${error.message}`);
      toast.error('Não foi possível abrir o chat deste destinatário');
      return;
    }
    const payload = data as { conversation_id?: string } | null;
    if (!payload?.conversation_id) {
      toast.error('Conversa não encontrada');
      return;
    }
    setChatConversationId(payload.conversation_id);
    setChatModalOpen(true);
  }, [currentTenant]);


  // Ações da campanha (todas via RPC update_campaign_status)
  const changeStatus = useCallback(async (newStatus: 'running' | 'paused' | 'cancelled', successMsg: string, errorMsg: string) => {
    if (!campaign) return;
    setActionLoading(true);
    try {
      const { error } = await supabase.rpc('update_campaign_status', {
        p_campaign_id: campaign.id,
        p_new_status: newStatus,
        p_tenant_hint: currentTenant?.id,
      });
      if (error) throw error;
      toast.success(successMsg);
      await fetchCampaignDetails();
    } catch (error) {
      logger.error('CampaignDetails', `${errorMsg}: ${error}`);
      toast.error(errorMsg);
    } finally {
      setActionLoading(false);
    }
  }, [campaign, currentTenant, fetchCampaignDetails]);

  const handleStart  = () => changeStatus('running',   'Campanha iniciada! O processamento iniciará em breve.', 'Erro ao iniciar campanha');
  const handlePause  = () => changeStatus('paused',    'Campanha pausada',                                       'Erro ao pausar campanha');
  const handleResume = () => changeStatus('running',   'Campanha retomada! O processamento continuará em breve.', 'Erro ao retomar campanha');
  const handleCancel = () => changeStatus('cancelled', 'Campanha cancelada',                                     'Erro ao cancelar campanha');

  // Calcular progresso
  const progress = stats.total > 0
    ? Math.round(((stats.sent + stats.delivered + stats.read + stats.failed + stats.skipped) / stats.total) * 100)
    : 0;

  const successRate = (stats.sent + stats.delivered + stats.read) > 0
    ? Math.round(((stats.delivered + stats.read) / (stats.sent + stats.delivered + stats.read)) * 100)
    : 0;

  // Loading state
  if (loading) {
    return <LoadingSkeleton />;
  }

  if (!campaign) {
    return null;
  }

  const status = statusConfig[campaign.status || 'draft'];

  return (
    <div className="space-y-4">
      {/* ========== HEADER COMPACTO (padrão Departments/TaskList) ========== */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 p-4 border-b">
        <div className="flex items-center gap-3 min-w-0 lg:max-w-[40%]">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={goBack}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>

          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-semibold tracking-tight truncate">{campaign.name}</h1>

              {/* Badge único de status — compacto, sem pulse, sem gradiente */}
              <Badge
                variant="outline"
                className={cn(
                  'gap-1 text-[10px] px-2 py-0.5 font-normal',
                  status.className
                )}
              >
                {status.dotClass && (
                  <span className={cn('w-1.5 h-1.5 rounded-full', status.dotClass)} />
                )}
                {status.label}
              </Badge>

              {/* Origem: listas importadas usadas no disparo (clicável -> preview da lista) */}
              {campaignLists.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => handleOpenListPreview(l.id)}
                  disabled={loadingPreviewListId === l.id}
                  title={`Ver detalhes da lista: ${l.name}`}
                >
                  <Badge
                    variant="outline"
                    className="gap-1 text-[10px] px-2 py-0.5 font-normal max-w-[220px] cursor-pointer hover:bg-muted transition-colors"
                  >
                    <ListChecks className="h-3 w-3 text-muted-foreground shrink-0" />
                    <span className="truncate">Lista: {l.name}</span>
                  </Badge>
                </button>
              ))}
            </div>


            {campaign.description && (
              <p className="text-xs text-muted-foreground mt-0.5 truncate">{campaign.description}</p>
            )}
          </div>
        </div>

        {/* Barra de progresso inline — ocupa o espaço entre título e ações */}
        <div className="flex-1 min-w-0 lg:px-4">
          <CampaignProgressBar
            variant="inline"
            progress={progress}
            successRate={successRate}
            startedAt={campaign.started_at}
            completedAt={campaign.completed_at}
            messageType={campaign.message_type}
          />
        </div>

        {/* Ações — padrão TaskList: icon-sm para refresh, xs para ações primárias */}
        <div className="flex items-center gap-2 lg:ml-0 ml-auto shrink-0">
          <Button
            variant="outline"
            size="icon-sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Atualizar"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')} />
          </Button>

          {campaign.status === 'draft' && (
            <Button
              size="xs"
              onClick={handleStart}
              disabled={actionLoading}
            >
              <Play className="h-3.5 w-3.5" />
              Iniciar
            </Button>
          )}

          {campaign.status === 'running' && (
            <Button
              variant="outline"
              size="xs"
              onClick={handlePause}
              disabled={actionLoading}
            >
              <Pause className="h-3.5 w-3.5" />
              Pausar
            </Button>
          )}

          {campaign.status === 'paused' && (
            <>
              <Button
                size="xs"
                onClick={handleResume}
                disabled={actionLoading}
              >
                <Play className="h-3.5 w-3.5" />
                Retomar
              </Button>
              <Button
                variant="destructive"
                size="xs"
                onClick={handleCancel}
                disabled={actionLoading}
              >
                <StopCircle className="h-3.5 w-3.5" />
                Cancelar
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ========== CARDS DE ESTATÍSTICAS (padrão Departments) ========== */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <CampaignMetricCard title="Total" value={stats.total} icon={Users} variant="primary" />
        <CampaignMetricCard title="Pendentes" value={stats.pending} icon={Clock} variant="neutral" />
        <CampaignMetricCard title="Enviados" value={stats.sent} icon={Send} variant="info" />
        <CampaignMetricCard title="Entregues" value={stats.delivered} icon={CheckCheck} variant="success" />
        <CampaignMetricCard title="Lidos" value={stats.read} icon={Eye} variant="purple" />
        <CampaignMetricCard title="Falhas" value={stats.failed} icon={XCircle} variant="danger" />
        <CampaignMetricCard title="Ignorados" value={stats.skipped} icon={AlertTriangle} variant="warning" />
      </div>

      {/* Barra de progresso movida para o header — economiza espaço vertical */}

      {/* ========== TABS MODERNOS ========== */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-muted/50 p-1">
          <TabsTrigger 
            value="recipients" 
            className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm"
          >
            <Users className="h-4 w-4" />
            Destinatários
            <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
              {stats.total}
            </Badge>
          </TabsTrigger>
          <TabsTrigger 
            value="message"
            className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm"
          >
            <MessageSquare className="h-4 w-4" />
            Mensagem
          </TabsTrigger>
          <TabsTrigger 
            value="logs"
            className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm"
          >
            <ScrollText className="h-4 w-4" />
            Logs
          </TabsTrigger>
          <TabsTrigger 
            value="settings"
            className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm"
          >
            <Settings2 className="h-4 w-4" />
            Configurações
          </TabsTrigger>
        </TabsList>

        {/* Tab: Destinatários */}
        <TabsContent value="recipients">
          <RecipientTable
            recipients={recipients}
            stats={stats}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            search={search}
            onSearchChange={setSearch}
            onRecipientClick={handleRecipientClick}
            loading={recipientsLoading}
            page={recipientsPage}
            pageSize={recipientsPageSize}
            total={recipientsTotal}
            onPageChange={setRecipientsPage}
            onPageSizeChange={(s) => { setRecipientsPageSize(s); setRecipientsPage(1); }}
            /* Exportação CSV/XLS respeitando o filtro ativo das sub-abas */
            exportSlot={
              id ? (
                <RecipientExportButton
                  campaignId={id}
                  campaignName={campaign.name}
                  statusFilter={statusFilter}
                  search={search}
                  total={recipientsTotal}
                />
              ) : null
            }
          />

        </TabsContent>

        {/* Tab: Mensagem - Com suporte a grupos */}
        <TabsContent value="message">
          <Card className="overflow-hidden">
            <CardHeader className="bg-muted/30 border-b">
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary" />
                Conteúdo da Mensagem
              </CardTitle>
              {messageGroups.length > 1 && (
                <p className="text-sm text-muted-foreground mt-1">
                  {messageGroups.length} grupos de mensagens - Sistema seleciona aleatoriamente para cada destinatário
                </p>
              )}
            </CardHeader>
            <CardContent className="p-6">
              {messageGroups.length > 0 ? (
                // Exibir grupos em accordion
                <Accordion type="multiple" defaultValue={messageGroups.map(g => g.id)} className="space-y-3">
                  {messageGroups.map((group) => {
                    const groupItems = messageItems.filter(item => item.group_id === group.id);
                    const totalWeight = messageGroups.reduce((sum, g) => sum + g.weight, 0);
                    const percentage = totalWeight > 0 ? Math.round((group.weight / totalWeight) * 100) : 0;
                    
                    // Obter tipos únicos de itens do grupo
                    const uniqueTypes = [...new Set(groupItems.map(item => item.item_type))];
                    
                    return (
                      <AccordionItem key={group.id} value={group.id} className="border rounded-lg overflow-hidden">
                        <AccordionTrigger className="px-4 py-3 bg-muted/30 hover:bg-muted/50 hover:no-underline">
                          <div className="flex items-center gap-3 w-full">
                            <span className="font-semibold">{group.name}</span>
                            <Badge variant="outline" className="bg-primary/10 text-primary">
                              {percentage}% chance
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {groupItems.length} {groupItems.length === 1 ? 'item' : 'itens'}
                            </span>
                            {/* Badges dos tipos de itens */}
                            <div className="flex gap-1 ml-auto mr-4">
                              {uniqueTypes.map(type => {
                                const config = itemTypeConfig[type];
                                if (!config) return null;
                                return (
                                  <Badge key={type} variant="outline" className={cn('gap-1 text-xs', config.color)}>
                                    {config.icon}
                                  </Badge>
                                );
                              })}
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pt-4 pb-4">
                          {groupItems.length > 0 ? (
                            <div className="space-y-4">
                              {groupItems.map((item, index) => (
                                <MessageItemPreview key={item.id} item={item} index={index} />
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground italic">
                              Nenhum item neste grupo
                            </p>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              ) : messageItems.length > 0 ? (
                // Fallback: exibir itens sem grupo (legado)
                <div className="space-y-4">
                  {messageItems.map((item, index) => (
                    <MessageItemPreview key={item.id} item={item} index={index} />
                  ))}
                </div>
              ) : (
                // Fallback: exibir conteúdo da campanha diretamente
                <div className="max-w-md">
                  <div className="bg-muted rounded-2xl rounded-tl-sm p-4 shadow-sm">
                    <p className="whitespace-pre-wrap">{campaign.message_content}</p>
                  </div>
                  {campaign.media_url && (
                    <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Mídia anexada:</p>
                      <a
                        href={campaign.media_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline text-sm"
                      >
                        {campaign.media_url}
                      </a>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Logs */}
        <TabsContent value="logs">
          <CampaignExecutionLogs campaignId={campaign.id} />
        </TabsContent>

        {/* =================================================================
            Tab: Configurações — Layout enterprise denso (Grid 8/4)
            ----------------------------------------------------------------
            - Coluna esquerda (col-span-8): Canal/Inboxes, Delay e Ações
            - Coluna direita (col-span-4): Grupo de Blocos, Opções, Histórico
            - Tokens semânticos (sem cores hardcoded). Padding reduzido.
            ================================================================= */}
        <TabsContent value="settings">
          <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
            {/* Header da aba */}
            <div className="px-5 py-3 border-b bg-muted/30 flex items-center justify-between">
              <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
                <Settings2 className="h-4 w-4 text-muted-foreground" />
                Configurações da Campanha
              </h2>
              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                Configuração ativa
              </span>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-12 gap-5">
              {/* ============== Coluna principal (8/12) ============== */}
              <div className="md:col-span-8 space-y-5">
                {/* Seção: Canal e Inboxes */}
                <section className="space-y-2">
                  <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                    <Inbox className="h-3 w-3" />
                    Canal e Caixas de Entrada
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="p-3 bg-muted/40 rounded-lg border">
                      <div className="text-[10px] text-muted-foreground font-semibold mb-1.5">
                        TIPO DE CANAL
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-500" />
                        <span className="text-sm font-medium text-foreground">
                          {campaign.channel_type === 'whatsapp' ? 'WhatsApp' :
                           campaign.channel_type === 'whatsapp_oficial' ? 'WhatsApp Oficial' :
                           campaign.channel_type}
                        </span>
                      </div>
                    </div>
                    <div className="p-3 bg-muted/40 rounded-lg border">
                      <div className="text-[10px] text-muted-foreground font-semibold mb-1.5">
                        CAIXAS DE ENTRADA
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {campaignInboxes.length > 0 ? (
                          campaignInboxes.map(inbox => (
                            <Badge key={inbox.inbox_id} variant="secondary" className="text-xs">
                              {inbox.inbox_name}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-muted-foreground italic">
                            Nenhuma inbox vinculada
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </section>

                {/* Seção: Delay (valores monoespaçados, técnico) */}
                <section className="space-y-2">
                  <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                    <Timer className="h-3 w-3" />
                    Configurações de Delay
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="p-3 border rounded-lg">
                      <div className="text-[10px] text-muted-foreground font-semibold mb-1">
                        DELAY ENTRE MENSAGENS
                      </div>
                      <div className="text-base font-mono font-semibold text-foreground">
                        {((campaign.delay_min_ms || 1000) / 1000).toFixed(1)}s - {((campaign.delay_max_ms || 3000) / 1000).toFixed(1)}s
                      </div>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <div className="text-[10px] text-muted-foreground font-semibold mb-1">
                        TAMANHO DO BLOCO
                      </div>
                      <div className="text-base font-mono font-semibold text-foreground">
                        {campaign.block_size || 5} msgs
                      </div>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <div className="text-[10px] text-muted-foreground font-semibold mb-1">
                        DELAY ENTRE BLOCOS
                      </div>
                      <div className="text-base font-mono font-semibold text-foreground">
                        {((campaign.block_delay_min_ms || 100) / 1000).toFixed(1)}s - {((campaign.block_delay_max_ms || 400) / 1000).toFixed(1)}s
                      </div>
                    </div>
                  </div>
                  {/* Sub-bloco: Parâmetros do Grupo de Blocos quando habilitado */}
                  {campaign.group_block_enabled && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                      <div className="p-3 border rounded-lg">
                        <div className="text-[10px] text-muted-foreground font-semibold mb-1">
                          MENSAGENS POR GRUPO
                        </div>
                        <div className="text-base font-mono font-semibold text-foreground">
                          {campaign.group_block_size || 20} msgs
                        </div>
                      </div>
                      <div className="p-3 border rounded-lg">
                        <div className="text-[10px] text-muted-foreground font-semibold mb-1">
                          INTERVALO ENTRE GRUPOS
                        </div>
                        <div className="text-base font-mono font-semibold text-foreground">
                          {campaign.group_block_interval_min_minutes || 10} - {campaign.group_block_interval_max_minutes || 20} min
                        </div>
                      </div>
                    </div>
                  )}
                </section>

                {/* Seção: Ações Pós-Disparo (tabela compacta) */}
                <section className="space-y-2">
                  <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                    <Zap className="h-3 w-3" />
                    Ações Pós-Disparo
                    <span className="ml-auto text-[10px] font-medium text-muted-foreground normal-case tracking-normal">
                      {campaignActions.length} {campaignActions.length === 1 ? 'ação' : 'ações'}
                    </span>
                  </h3>
                  {campaignActions.length > 0 ? (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-muted/40 text-[10px] text-muted-foreground uppercase font-bold border-b">
                          <tr>
                            <th className="px-3 py-2 w-10">#</th>
                            <th className="px-3 py-2">Tipo</th>
                            <th className="px-3 py-2">Descrição</th>
                            <th className="px-3 py-2 text-right">Condição</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {campaignActions.map((action, index) => {
                            const config = campaignActionConfig[action.action_type];
                            const conditionConfig = executionConditionConfig[action.execution_condition];
                            const Icon = config.icon;
                            const ConditionIcon = conditionConfig.icon;
                            // Resolve nome amigável conforme tipo de ação
                            const getResolvedName = (): string | null => {
                              const value = action.action_value;
                              if (action.action_type === 'add_tag' && value.tag_id) {
                                return actionResolvedNames[`tag_${value.tag_id}`] || null;
                              } else if (action.action_type === 'assign_phase' && value.phase_id) {
                                return actionResolvedNames[`phase_${value.phase_id}`] || null;
                              } else if (action.action_type === 'assign_department' && value.department_id) {
                                return actionResolvedNames[`department_${value.department_id}`] || null;
                              } else if (action.action_type === 'assign_agent' && value.agent_id) {
                                return actionResolvedNames[`agent_${value.agent_id}`] || null;
                              }
                              return null;
                            };
                            const resolvedName = getResolvedName();
                            return (
                              <tr key={action.id} className="hover:bg-muted/30 transition-colors">
                                <td className="px-3 py-2 text-muted-foreground font-mono text-xs">
                                  {String(index + 1).padStart(2, '0')}
                                </td>
                                <td className="px-3 py-2">
                                  <div className="flex items-center gap-2">
                                    <Icon className={cn("h-3.5 w-3.5", config.iconColor)} />
                                    <span className="text-sm font-medium text-foreground">{config.label}</span>
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-sm text-muted-foreground truncate max-w-[260px]">
                                  {resolvedName || config.description}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  <Badge variant="outline" className={cn("gap-1 text-[10px] uppercase font-bold", conditionConfig.color)}>
                                    <ConditionIcon className="h-3 w-3" />
                                    {conditionConfig.label}
                                  </Badge>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="border rounded-lg p-6 text-center bg-muted/20">
                      <Zap className="h-6 w-6 text-muted-foreground/40 mx-auto mb-2" />
                      <p className="text-xs text-muted-foreground">
                        Nenhuma ação pós-disparo configurada.
                      </p>
                    </div>
                  )}
                </section>
              </div>

              {/* ============== Coluna lateral (4/12) ============== */}
              <div className="md:col-span-4 space-y-5">
                {/* Card: Grupo de Blocos */}
                <section className="p-3 bg-muted/40 border rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                      <Package className="h-3 w-3" />
                      Grupo de Blocos
                    </h3>
                    <Badge
                      variant={campaign.group_block_enabled ? 'default' : 'secondary'}
                      className="text-[10px] uppercase font-bold px-1.5 py-0"
                    >
                      {campaign.group_block_enabled ? 'Habilitado' : 'Desabilitado'}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {campaign.group_block_enabled
                      ? 'Envios pausados em grupos conforme parâmetros configurados.'
                      : 'Envio contínuo sem pausas entre grupos de blocos.'}
                  </p>
                </section>

                {/* Opções de Envio (checkbox-style readonly) */}
                <section className="space-y-2">
                  <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                    <Settings2 className="h-3 w-3" />
                    Opções de Envio
                  </h3>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2.5 p-2 bg-card border rounded-lg">
                      <div className={cn(
                        "w-4 h-4 rounded flex items-center justify-center flex-shrink-0",
                        campaign.use_contact_name ? "bg-primary" : "border border-input"
                      )}>
                        {campaign.use_contact_name && (
                          <CheckCheck className="h-3 w-3 text-primary-foreground" />
                        )}
                      </div>
                      <span className="text-xs font-medium text-foreground">Usar nome do contato</span>
                    </div>
                    <div className="flex items-center gap-2.5 p-2 bg-card border rounded-lg">
                      <div className={cn(
                        "w-4 h-4 rounded flex items-center justify-center flex-shrink-0",
                        campaign.verify_whatsapp ? "bg-primary" : "border border-input"
                      )}>
                        {campaign.verify_whatsapp && (
                          <CheckCheck className="h-3 w-3 text-primary-foreground" />
                        )}
                      </div>
                      <span className="text-xs font-medium text-foreground">Verificar WhatsApp</span>
                    </div>
                  </div>
                </section>

                {/* Histórico — timeline vertical compacta */}
                <section className="space-y-2">
                  <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                    <Calendar className="h-3 w-3" />
                    Histórico
                  </h3>
                  <div className="relative space-y-3 pl-4 border-l-2 ml-1.5">
                    {[
                      { label: 'Criada', value: campaign.created_at, active: !!campaign.created_at },
                      { label: 'Agendada', value: campaign.scheduled_at, active: !!campaign.scheduled_at },
                      { label: 'Iniciada', value: campaign.started_at, active: !!campaign.started_at },
                      { label: 'Concluída', value: campaign.completed_at, active: !!campaign.completed_at },
                    ].map((item) => (
                      <div key={item.label} className="relative">
                        <div className={cn(
                          "absolute -left-[21px] mt-1 w-2.5 h-2.5 rounded-full border-2 border-background",
                          item.active ? "bg-primary ring-2 ring-primary/20" : "bg-muted"
                        )} />
                        <div className={cn(
                          "text-[11px] font-bold",
                          item.active ? "text-foreground" : "text-muted-foreground"
                        )}>
                          {item.label}
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {item.value
                            ? format(new Date(item.value), "dd/MM/yyyy HH:mm", { locale: ptBR })
                            : '—'}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Modal de chat aberto na inbox de disparo do destinatário */}
      <ChatConversationModal
        conversationId={chatConversationId}
        open={chatModalOpen}
        onOpenChange={(open) => {
          setChatModalOpen(open);
          if (!open) setChatConversationId(null);
        }}
      />

      {/* Detalhes da lista importada usada no disparo */}
      {previewList && (
        <ListPreviewDialog
          list={previewList}
          open={!!previewList}
          onOpenChange={(open) => !open && setPreviewList(null)}
        />
      )}
    </div>
  );
}

/**
 * Página de detalhes da campanha (rota /tenant/campaigns/:id).
 * Invólucro fino sobre CampaignDetailsContent — sem mudança de comportamento.
 */
export default function CampaignDetails() {
  return <CampaignDetailsContent variant="page" />;
}
